import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '../hooks/useTranslation.js';
import { requestJson } from '../utils/authClient.js';
import { AdminSection, ShowMore, useShowMore, writeAdminRoute } from '../components/AdminNav.jsx';
import { formatBerlinDay, startOfBerlinDay } from './VeridionShared.jsx';

// The company's day (IDD D-HP-ADMIN-036): the timeline Veridion Core keeps of what happened, read newest first
// through the Core's read-only timeline door, with a choice of the window (today, 24 hours, 7 days) and of one kind.
// A happening on a work item leads into the graph section, which draws that work item.
const API = '/api/admin/veridion-core/timeline';
const LIMIT = 300;

// "Today" is the Berlin day, whatever the viewer's browser zone.
const sinceOf = (choice) => {
  if (choice === 'today') return startOfBerlinDay();
  if (choice === 'week') return new Date(Date.now() - 7 * 24 * 3600 * 1000);
  return new Date(Date.now() - 24 * 3600 * 1000);
};
const formatInteger = (value) => (value === null || value === undefined || value === '' ? '-' : Number(value).toLocaleString());
const formatDay = formatBerlinDay;

// `fixedKind` narrows the section to one kind for good (the research page shows the research alone); `title`
// and `subtitle` name that page. The newest fifty happenings stand on the page, the rest on request.
export const MemoryDay = ({ fixedKind = '', title = '', subtitle = '' }) => {
  const { t } = useTranslation();
  const m = t.admin.memory;
  const d = m.day;
  const [choice, setChoice] = useState('today');
  const [chosenKind, setKind] = useState('');
  const kind = fixedKind || chosenKind;
  const [shown, more] = useShowMore(25);
  const [kinds, setKinds] = useState([]);
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const since = sinceOf(choice).toISOString();
      const data = await requestJson(`${API}?since=${encodeURIComponent(since)}&limit=${LIMIT}&kind=${encodeURIComponent(kind)}`);
      setItems(data.items || []);
      setKinds(data.kinds || []);
      setError('');
    } catch (failure) {
      setError(failure?.code || 'unavailable');
    } finally {
      setBusy(false);
    }
  }, [choice, kind]);

  useEffect(() => { load(); }, [load]);

  // The address carries the work item as a third segment, which the graph section reads and draws.
  const showWork = (workItem) => writeAdminRoute('company-memory', 'graph', workItem);
  const kindLabel = (value) => d.kinds?.[value] || value;
  const today = formatDay(new Date());

  return (
    <AdminSection id={fixedKind ? `memory-day-${fixedKind}` : 'memory-day'} title={title || d.title} subtitle={subtitle || d.subtitle}
      count={items.length} state={busy ? m.loading : ''}
      actions={(
        <form className="flex flex-wrap gap-2" onSubmit={(event) => { event.preventDefault(); load(); }}>
          <select className="form-input" value={choice} onChange={(event) => setChoice(event.target.value)}>
            <option value="today">{d.sinceToday}</option>
            <option value="day">{d.sinceDay}</option>
            <option value="week">{d.sinceWeek}</option>
          </select>
          {!fixedKind ? (
            <select className="form-input" value={chosenKind} onChange={(event) => setKind(event.target.value)}>
              <option value="">{d.allKinds}</option>
              {kinds.map((value) => <option key={value} value={value}>{kindLabel(value)}</option>)}
            </select>
          ) : null}
          <button type="submit" className="homepage-button-secondary" disabled={busy}>{d.refresh}</button>
        </form>
      )}>
      {error ? <div className="homepage-muted-panel p-4 text-sm text-error">{m.unavailable} ({error})</div> : null}
      <div className="admin-table-shell"><div className="admin-table-scroll">
        <table className="admin-table">
          <thead><tr>
            <th>{d.time}</th><th>{m.kind}</th><th>{d.actor}</th><th>{d.happening}</th><th>{d.work}</th>
          </tr></thead>
          <tbody>
            {items.slice(0, shown).map((item) => {
              const day = formatDay(item.at);
              return (
                <tr key={item.id}>
                  <td className="whitespace-nowrap">{day && day !== today ? `${day} ` : ''}{item.time || '-'}</td>
                  <td>{kindLabel(item.kind)}{item.event && item.event !== item.kind ? <span className="text-text-secondary"> · {item.event}</span> : null}</td>
                  <td className="break-words">{item.who || item.actor || '-'}</td>
                  <td className="break-words"><span style={{ whiteSpace: 'pre-wrap' }}>{item.text}</span></td>
                  <td className="break-words">
                    {item.work_item ? (
                      <button type="button" className="admin-link" onClick={() => showWork(item.work_item)}>{item.title || item.work_item}</button>
                    ) : (item.title || '-')}
                  </td>
                </tr>
              );
            })}
            {!items.length && !busy ? <tr><td colSpan={5}>{d.empty}</td></tr> : null}
          </tbody>
        </table>
      </div></div>
      <ShowMore shown={shown} total={items.length} onMore={more} />
    </AdminSection>
  );
};

export default MemoryDay;
